/**
 * Luxury Listing — Navy & Brass
 *
 * Archetype: full-bleed twilight hero over a deep navy field, with a brass
 * price plate breaking the seam between photograph and type. The break is the
 * whole idea — it stops the page reading as two stacked rectangles and gives
 * the eye a single, unmissable entry point.
 */

import { CANVAS, SPACE, TYPE, RULE } from '../../system/tokens';
import { getPalette, scrim } from '../../system/palettes';
import { roles } from '../../system/typography';
import { grid } from '../../system/grid';
import { text, shape, rule, image, page } from '../../system/elements';
import { scrimDown, statRow, priceBadge } from '../../system/blocks';
import { PHOTOS } from '../../system/photos';

const ID = 'tpl-flyer-real-estate-01';

export default {
  id: ID,
  meta: {
    name: 'Luxury Listing — Navy & Brass',
    collection: 'signature',
    archetype: 'full-bleed-hero-seam',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'navy-brass',
    pairing: 'playfair-inter',
    tags: ['real estate', 'flyer', 'luxury', 'listing', 'navy', 'gold', 'premium', 'us letter'],
    description: 'Twilight hero photograph over a deep navy field with a brass price plate breaking the seam. For high-end single-property listings.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('navy-brass');
    const r = roles('playfair-inter');
    const g = grid(canvas);
    const els = [];

    const PHOTO_H = 1420;

    // ── Hero photograph, full bleed ─────────────────────────────────────────
    els.push(image(`${ID}-hero`, { x: 0, y: 0, width: canvas.width, height: PHOTO_H, src: PHOTOS.twilightChalet }));

    // Scrim so the eyebrow stays readable over any replacement photo.
    els.push(scrimDown(`${ID}-hero-scrim`, {
      x: 0, y: PHOTO_H - 700, width: canvas.width, height: 700, color: p.bg, to: 0.92,
    }));

    // ── Brand line, top-left over the photograph ────────────────────────────
    els.push(shape(`${ID}-brandmark`, {
      x: g.left, y: g.margin, width: RULE.thick, height: 120, fill: p.accent,
    }));
    els.push(text(`${ID}-brand`, r.eyebrow, 'Harbour & Vine\nEstates', {
      x: g.left + SPACE.xs + RULE.thick, y: g.margin + 6, width: 900,
    }, { color: p.ink, fontSize: TYPE.caption }));

    // ── Brass price plate, breaking the photo/field seam ────────────────────
    const price = priceBadge(ID, r, {
      x: g.left, y: PHOTO_H - 200, copy: '$1,285,000',
      fill: p.accent, inkColor: p.accentInk, width: 840, height: 200,
    });
    els.push(...price.elements);

    // ── Editorial block on the navy field ───────────────────────────────────
    let y = PHOTO_H + SPACE.lg;

    const eyebrow = text(`${ID}-status`, r.eyebrow, 'Just Listed · Hudson Valley', {
      x: g.left, y, width: g.contentWidth,
    }, { color: p.accent });
    els.push(eyebrow);
    y += eyebrow.height + SPACE.sm;

    const headline = text(`${ID}-headline`, r.h1, 'The Ridgeline\nResidence', {
      x: g.left, y, width: g.contentWidth,
    }, { color: p.ink, fontSize: 183 });
    els.push(headline);
    y += headline.height + SPACE.sm;

    els.push(rule(`${ID}-rule`, { x: g.left, y, width: 380, weight: RULE.medium, color: p.accent }));
    y += RULE.medium + SPACE.md;

    const blurb = text(`${ID}-blurb`, r.body,
      '412 Ridgeline Drive, Cold Spring, NY 10516\nCedar and glass on four private acres, with a\ndouble-height great room and river views.', {
        x: g.left, y, width: g.span(9),
      }, { color: p.inkMuted });
    els.push(blurb);
    y += blurb.height + SPACE.lg;

    // ── Specification row ───────────────────────────────────────────────────
    const stats = statRow(ID, r, {
      x: g.left, y, width: g.contentWidth,
      stats: [
        { value: '5', label: 'Bedrooms' },
        { value: '4.5', label: 'Bathrooms' },
        { value: '4,280', label: 'Sq Ft' },
        { value: '4.0', label: 'Acres' },
      ],
      valueColor: p.ink, labelColor: p.inkMuted, dividerColor: scrim(p.ink, 0.22),
    });
    els.push(...stats.elements);

    // ── Footer, pinned to the foot so the stack above can breathe ───────────
    // Height is the tallest column (the three contact lines) plus the rule gap,
    // measured back from the bottom margin.
    const contactLines = 3;
    const footBlockH = SPACE.md + TYPE.body * 1.5 * contactLines;
    const footY = canvas.height - g.margin - footBlockH;

    els.push(rule(`${ID}-footer-rule`, { x: g.left, y: footY, width: g.contentWidth, weight: RULE.hair, color: scrim(p.ink, 0.28) }));
    const footTextY = footY + SPACE.md;

    els.push(text(`${ID}-agent`, r.h3, 'Marielle Vance', { x: g.left, y: footTextY, width: g.span(6) }, { color: p.ink }));
    els.push(text(`${ID}-agent-meta`, r.caption, 'Licensed Associate Broker · Lic. #10301218873', {
      x: g.left, y: footTextY + TYPE.h3 * 1.25 + SPACE.xxs, width: g.span(6),
    }, { color: p.inkMuted }));

    els.push(text(`${ID}-contact`, r.body, '(845) 555-0147\nmarielle@harbourvine.com\nharbourvine.com', {
      x: g.x(7), y: footTextY, width: g.span(5), align: 'right',
    }, { color: p.inkMuted }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.bg, elements: els })],
    };
  },
};
